import { Button } from '@morressier/alembic';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider
} from 'react-router-dom';
import { health } from 'src/api/queries';
import FloatingNav from 'src/components/FloatingNav';
import Dashboard from 'src/layouts/Dashboard';
import InvitationPreview from '../pages/InvitationPreview';
import SubmissionDetails from '../pages/SubmissionDetails';
import NotFound from '../pages/Status/NotFound';
import JournalRoutes from './JournalRoutes';
import OrganizationRoutes from './OrganizationRoutes';
import UserRoutes from './UserRoutes';
import WorkspaceRoutes from './Workspace';
import DeclineReview from 'src/pages/DeclineReview';
import AuthRealmAccessControl from './AuthRealmAccessControl';
import { useState } from 'react';
import AuthHOC from '../components/AuthHOC';
import BrandDataProvider from 'src/contexts/BrandConfigProvider';
import DeclineCoReview from 'src/pages/DeclineReview/DeclineCoReview';
import TemplateVariablesTest from 'src/pages/LandingPage/TemplateVariablesTest';
import { ReviewerInvitation } from 'src/pages/ReviewerInvitation';
import {
  AuthRealmContext,
  getBrandConfigForAuthRealm,
  getLoginUrlForAuthRealm
} from './AuthRealmAccessControl/AuthRealmContext';
import { clientAuthRealm } from '../utils/env';
import OwnCopyrightForm from 'src/pages/Status/OwnCopyrightForm';
import SignUpLayout from './SignUpLayout';
import UserDetailsForm from 'src/pages/Sign-Up/UserDetailsForm';
import PasswordResetInviteSent from '../pages/SignIn/PasswordResetInviteSent';
import ResetPassword from '../pages/SignIn/ResetPassword';
import VerifyPrimaryEmail from 'src/pages/VerifyPrimaryEmail';
import SignUpInviteSent from 'src/pages/SignUpInviteSent';
import TrackingWrapper from 'src/utils/TrackingWrapper';
import { DeclineReviews } from '../pages/DeclineReview/DeclineReviews';
import AccountActivated from 'src/pages/AccountActivated';
import { AcceptWorkspaceInvitation } from 'src/pages/AcceptWorkspaceInvitation/AcceptWorkspaceInvitation';
import { ForgotPassword } from 'src/pages/SignIn/ForgotPassword';
import AssetAccess from './Workspace/AssetAccess';
import { SignUpForm } from 'src/pages/Sign-Up/SignUpForm';
import { AcceptReviews } from '../pages/AcceptReviews';
import HomePage from '../pages/AppHome';
import FileViewer from 'src/pages/FileViewer';
import ErrorPage from 'src/ErrorPage';

// Replace with API call
const AuthRealmEntryPoint = () => {
  if (!clientAuthRealm) {
    return <NotFound />;
  }

  // Once we replaced isValidAuthRealm() with API call,
  // introduce loading state and set authRealm on success.
  const [authRealm] = useState(clientAuthRealm);
  const routes = getRoutes();

  return (
    <AuthRealmContext.Provider
      value={{
        authRealm,
        loginUrl: getLoginUrlForAuthRealm(authRealm),
        clientBrand: getBrandConfigForAuthRealm(authRealm),
        getLoginUrlForAuthRealm
      }}
    >
      <RouterProvider router={routes} />
    </AuthRealmContext.Provider>
  );
};

const getRoutes = () =>
  createBrowserRouter(
    createRoutesFromElements(
      <Route errorElement={<ErrorPage />}>
        <>
          <Route
            path="/"
            element={
              <TrackingWrapper>
                <AuthHOC>
                  <>
                    <FloatingNav />
                    <AuthRealmAccessControl>
                      <BrandDataProvider>
                        <Outlet />
                      </BrandDataProvider>
                    </AuthRealmAccessControl>
                  </>
                </AuthHOC>
              </TrackingWrapper>
            }
          >
            <Route path="_404" element={<NotFound />} />
            <Route
              path="debug"
              element={
                <div>
                  <Button
                    onClick={health}
                    title="verify connection to the service"
                  >
                    Health Check
                  </Button>
                </div>
              }
            />

            {OrganizationRoutes}
            {JournalRoutes}

            <Route index element={<Navigate to={`app/home`} />} />

            <Route
              path={'app'}
              element={
                <Dashboard>
                  <Dashboard.MainArea>
                    <Outlet />
                  </Dashboard.MainArea>
                </Dashboard>
              }
            >
              {UserRoutes}
              {WorkspaceRoutes}
              <Route
                path="template-variables-test"
                element={<TemplateVariablesTest />}
              />
              <Route path="home" element={<HomePage />} />
            </Route>
            <Route
              path="app/accept-workspace-invitation"
              element={<AcceptWorkspaceInvitation />}
            />
            <Route
              path="app/reviews/:id/invite"
              element={<ReviewerInvitation />}
            />
            <Route path="file-viewer" element={<FileViewer />} />
          </Route>

          <Route
            path="submission/*"
            element={
              <AuthHOC>
                <SubmissionDetails />
              </AuthHOC>
            }
          />
          <Route
            path="invitation-preview"
            element={
              <AuthHOC>
                <InvitationPreview />
              </AuthHOC>
            }
          />
          <Route
            path="asset-access"
            element={
              <AuthHOC>
                <AssetAccess />
              </AuthHOC>
            }
          />

          {/* Unauthenticated route */}
          <Route path="app/reviews/:id/decline" element={<DeclineReviews />} />
          <Route path="app/reviews/:id/accept" element={<AcceptReviews />} />

          <Route
            path="reviews/:id/decline-coreview"
            element={<DeclineCoReview />}
          />

          <Route path="reviews/:id/decline" element={<DeclineReview />} />
          <Route path="copyright_agreement" element={<OwnCopyrightForm />} />
          <Route path="signup" element={<SignUpLayout />}>
            <Route index element={<SignUpForm />} />
            <Route path="invite-sent/:email" element={<SignUpInviteSent />} />
            <Route
              path="account-activated/:userId"
              element={<AccountActivated />}
            />
            <Route path=":userId" element={<UserDetailsForm />} />
          </Route>

          <Route path="signin" element={<SignUpLayout />}>
            <Route
              path="*"
              element={<Navigate to="forgotpassword" replace />}
            />
            <Route path="forgotpassword" element={<ForgotPassword />} />
            <Route
              path="reset-sent/:email"
              element={<PasswordResetInviteSent />}
            />
          </Route>
          <Route path="signin/reset-password" element={<ResetPassword />} />
          <Route
            path="verify-primary-email"
            element={
              <AuthHOC>
                <VerifyPrimaryEmail />
              </AuthHOC>
            }
          />
          <Route
            path="reviews/:id/decline-review"
            element={<DeclineReview />}
          />
        </>
      </Route>
    ),
    {
      basename: `/`
    }
  );

export default AuthRealmEntryPoint;
